import { differenceInMinutes } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { getLongDuration } from 'lib/time'
import { Work } from '../types'
import { getRemovedReason } from './getRemovedReason'

type GetStatusOptions = {
  status: Work['status']
  startedAt?: string
  timezone: string
  completedAt?: string
  removedReason?: Work['removedReason']
}

export type ShiftStatusKind =
  | 'clocked_in'
  | 'time_worked'
  | 'missing_times'
  | 'removed'

export type ShiftStatusResult = {
  text: string
  kind: ShiftStatusKind
}

export const getShiftStatus = ({
  status,
  startedAt,
  timezone,
  completedAt,
  removedReason,
}: GetStatusOptions): ShiftStatusResult | undefined => {
  if (status === 'started') {
    const workerClockedInAt = startedAt
      ? formatInTimeZone(startedAt, timezone, 'h:mm a z')
      : undefined

    return workerClockedInAt
      ? { text: `Clocked in at ${workerClockedInAt}`, kind: 'clocked_in' }
      : undefined
  }

  if (
    status &&
    [
      'completed',
      'approved',
      'paid',
      'employer_approved',
      'admin_review',
      'employer_review',
    ].includes(status)
  ) {
    if (!startedAt || !completedAt) {
      return { text: '-', kind: 'missing_times' }
    }

    const startTime = formatInTimeZone(startedAt, timezone, 'h:mm a')
    const endTime = formatInTimeZone(completedAt, timezone, 'h:mm a z')
    const estimatedTimeWorkedInMinutes = differenceInMinutes(
      completedAt,
      startedAt
    )
    const estimatedTimeWorked = getLongDuration(estimatedTimeWorkedInMinutes)

    return startTime && endTime
      ? {
          text: `${startTime} - ${endTime} (${estimatedTimeWorked})`,
          kind: 'time_worked',
        }
      : undefined
  }

  if (status === 'removed') {
    return {
      text: removedReason ? getRemovedReason(removedReason) : '',
      kind: 'removed',
    }
  }

  return undefined
}
